<template>
	<section class="section-card">
		<div class="section-head">
			<h2 class="section-title">三、关键指标变化趋势</h2>
		</div>
		<p class="section-desc">同一设备关键指标变化趋势。</p>

		<div class="trend-grid">
			<div class="trend-chart-card" v-for="(item, index) in trends" :key="item.key">
				<div class="trend-chart-card__header">
					<div class="trend-chart-card__title">
						<span>{{ item.label }}</span>
						<span class="trend-chart-card__type-badge" :class="item.type">
							{{ getTypeLabel(item.type) }}
						</span>
					</div>
					<span class="trend-chart-card__status" :class="getDisplayStatusClass(item)">
						{{ getDisplayStatusText(item) }}
					</span>
				</div>

				<div :ref="'chart_' + index" class="chart-canvas"></div>

				<div class="trend-chart-card__footer">
					<span>
						当前值
						<strong>{{ item.currentValue }}</strong>
					</span>
					<span>
						历史基线 均值
						<strong>{{ item.historyMean }}</strong>
						/ σ
						<strong>{{ item.sigma }}</strong>
						/ 偏离
						<strong :class="item.deviation > 0 ? 'up' : 'down'">{{ formatDev(item.deviation) }}</strong>
					</span>
					<span>
						偏离天数
						<strong>{{ item.deviationDays }}</strong>
					</span>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
const echarts = require('echarts/lib/echarts')
require('echarts/lib/chart/line')
require('echarts/lib/component/grid')
require('echarts/lib/component/tooltip')
require('echarts/lib/component/markLine')

export default {
	name: 'TrendSection',
	props: {
		profileData: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			chartInstances: [],
		}
	},
	computed: {
		trends() {
			return (this.profileData && this.profileData.trends) || []
		},
	},
	watch: {
		'profileData.trends': {
			handler() {
				this.$nextTick(() => {
					this.renderAllCharts()
				})
			},
			immediate: true,
		},
	},
	mounted() {
		window.addEventListener('resize', this.handleResize)
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize)
		this.chartInstances.forEach(chart => {
			if (chart) chart.dispose()
		})
		this.chartInstances = []
	},
	methods: {
		renderAllCharts() {
			this.trends.forEach((item, index) => {
				this.$nextTick(() => {
					this.renderChart(item, index)
				})
			})
		},
		renderChart(item, index) {
			const refKey = 'chart_' + index
			const el = this.$refs[refKey]
			if (!el || !el[0]) return

			const dom = el[0]
			let chart = this.chartInstances[index]
			if (!chart) {
				chart = echarts.init(dom, null, { renderer: 'canvas' })
				this.$set(this.chartInstances, index, chart)
			}

			const dates = (item.data || []).map(d => d.date)
			const values = (item.data || []).map(d => d.value)
			const lineColor = this.getTrendColor(item)

			chart.setOption(
				{
					animation: false,
					grid: { top: 16, right: 16, bottom: 28, left: 48 },
					tooltip: {
						trigger: 'axis',
						backgroundColor: 'rgba(15, 23, 42, 0.92)',
						borderWidth: 0,
						textStyle: { color: '#f8fafc', fontSize: 12 },
					},
					xAxis: {
						type: 'category',
						data: dates,
						boundaryGap: false,
						axisLine: { lineStyle: { color: '#e2e8f0' } },
						axisTick: { show: false },
						axisLabel: { color: '#64748b', fontSize: 11 },
					},
					yAxis: {
						type: 'value',
						axisLine: { show: false },
						axisTick: { show: false },
						axisLabel: { color: '#64748b', fontSize: 11 },
						splitLine: { lineStyle: { color: '#e2e8f0', type: 'dashed' } },
					},
					series: [
						{
							type: 'line',
							data: values,
							smooth: true,
							symbol: 'circle',
							symbolSize: 8,
							lineStyle: {
								width: 2,
								color: lineColor,
							},
							itemStyle: {
								color: lineColor,
								borderColor: '#fff',
								borderWidth: 2,
							},
							areaStyle: {
								color: {
									type: 'linear',
									x: 0,
									y: 0,
									x2: 0,
									y2: 1,
									colorStops: [
										{
											offset: 0,
											color: this.getTrendAreaColor(item),
										},
										{ offset: 1, color: 'rgba(255,255,255,0)' },
									],
								},
							},
							markLine: {
								silent: true,
								symbol: 'none',
								lineStyle: { color: '#f59e0b', type: 'dashed', width: 1 },
								data: [{ yAxis: item.historyMean, label: { show: false } }],
							},
						},
					],
				},
				true,
			)
		},
		handleResize() {
			this.chartInstances.forEach(chart => {
				if (chart) chart.resize()
			})
		},
		getTypeLabel(type) {
			const map = { pressure: '压力', flow: '流量', temperature: '温度' }
			return map[type] || ''
		},
		getDisplayStatusText(item) {
			const label = String((item && item.displayLabel) || '')
			if (label.includes('偏高')) return '偏高'
			if (label.includes('偏低')) return '偏低'
			if (label === '偏离' || label === '异常') return label
			return '正常'
		},
		getDisplayStatusClass(item) {
			const label = String((item && item.displayLabel) || '')
			if (label.includes('偏高') || label === '偏离' || label === '异常') return 'up'
			if (label.includes('偏低')) return 'down'
			return 'normal'
		},
		getTrendColor(item) {
			const statusClass = this.getDisplayStatusClass(item)
			if (statusClass === 'up') return '#dc2626'
			if (statusClass === 'down') return '#2563eb'
			return '#16a34a'
		},
		getTrendAreaColor(item) {
			const statusClass = this.getDisplayStatusClass(item)
			if (statusClass === 'up') return 'rgba(220, 38, 38, 0.12)'
			if (statusClass === 'down') return 'rgba(37, 99, 235, 0.12)'
			return 'rgba(22, 163, 74, 0.12)'
		},
		formatDev(value) {
			if (value === null || value === undefined) return '--'
			const prefix = value > 0 ? '+' : ''
			return `${prefix}${value.toFixed(2)}σ`
		},
	},
}
</script>

<style lang="scss" scoped>
.section-card {
	position: relative;
	padding: 22px 24px 18px;
	background: var(--panel-bg);
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	box-shadow: var(--panel-shadow);
	backdrop-filter: blur(12px);
}

.section-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 8px;
}

.section-title {
	margin: 0;
	color: var(--title);
	font-size: 17px;
	font-weight: 700;
	letter-spacing: 0.01em;
}

.section-desc {
	margin: 0 0 16px;
	font-size: 13px;
	color: var(--muted);
	line-height: 1.6;
}

.trend-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 14px;
}

.trend-chart-card {
	border-radius: 10px;
	border: 1px solid var(--line);
	padding: 16px;
	background: #fff;
}

.trend-chart-card__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
}

.trend-chart-card__title {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 14px;
	font-weight: 600;
	color: var(--title);
}

.trend-chart-card__type-badge {
	font-size: 11px;
	font-weight: 500;
	padding: 1px 6px;
	border-radius: 6px;

	&.pressure {
		color: #2563eb;
		background: #dbeafe;
	}
	&.flow {
		color: #16a34a;
		background: #dcfce7;
	}
	&.temperature {
		color: #f59e0b;
		background: #fef3c7;
	}
}

.trend-chart-card__status {
	font-size: 12px;
	font-weight: 600;
	padding: 2px 8px;
	border-radius: 8px;

	&.up {
		color: #dc2626;
		background: #fee2e2;
	}
	&.down {
		color: #2563eb;
		background: #dbeafe;
	}
	&.normal {
		color: #16a34a;
		background: #dcfce7;
	}
}

.chart-canvas {
	width: 100%;
	height: 200px;
}

.trend-chart-card__footer {
	display: flex;
	flex-wrap: wrap;
	gap: 8px 16px;
	margin-top: 8px;
	padding-top: 8px;
	border-top: 1px solid var(--line);
	font-size: 12px;
	color: var(--muted);

	strong {
		color: var(--text);
		font-weight: 600;

		&.up {
			color: #dc2626;
		}
		&.down {
			color: #2563eb;
		}
	}
}

@media (max-width: 1240px) {
	.trend-grid {
		grid-template-columns: repeat(2, 1fr);
	}
}

@media (max-width: 900px) {
	.trend-grid {
		grid-template-columns: 1fr;
	}
	.chart-canvas {
		height: 180px;
	}
}
</style>
